import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    X, Star, StickyNote, Layers, MessagesSquare, BookOpen, Lightbulb,
    Target, GraduationCap, Brain, Loader2, RefreshCw, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Markdown } from "@/components/common/Markdown";
import { DetailLevel } from "@/components/common/DetailLevel";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { aiApi, termsApi, flashcardsApi, getAiDetail, setAiDetail } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

// Session-level cache so reopening a term (or picking a related one twice) is
// instant. The backend also caches per (user, doc, term, language).
const cache = new Map();
const normalize = (t) => (t || "").toLowerCase().split(/\s+/).join(" ").trim().slice(0, 200);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Subtle, fast staggered reveal — premium but never slow or restless.
const fieldContainer = { hidden: {}, show: { transition: { staggerChildren: 0.04, delayChildren: 0.02 } } };
const fieldItem = { hidden: { opacity: 0, y: 5 }, show: { opacity: 1, y: 0, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } } };

const Field = ({ icon: Icon, label, accent, children }) => (
    <motion.div variants={fieldItem} className="flex gap-2.5">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ background: `${accent}22`, color: accent }}>
            <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
            <div className="mono-label text-t4">{label}</div>
            <p className="mt-0.5 text-sm leading-relaxed text-t2">{children}</p>
        </div>
    </motion.div>
);

const ActionBtn = ({ icon: Icon, label, onClick, active, busy, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        disabled={busy}
        className={cn(
            "flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-[11px] transition-colors",
            active ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]" : "text-t3 hover:bg-surface-2 hover:text-t1"
        )}
    >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className={cn("h-4 w-4", active && "fill-current")} />}
        <span className="truncate">{label}</span>
    </button>
);

function ExplainBody({ docId, term, context, wordIndex, onAskAI, onClose, onPickRelated }) {
    const { t, lang } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [resp, setResp] = useState(null);
    const [saved, setSaved] = useState(false);
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState("");
    const [detail, setDetail] = useState(getAiDetail());

    const fetchIt = useCallback(async (refresh = false, dtl = detail) => {
        const key = `${docId}|${normalize(term)}|${lang}|${dtl}`;
        if (!refresh && cache.has(key)) { setResp(cache.get(key)); setLoading(false); setError(false); return; }
        setLoading(true); setError(false);
        try {
            const r = await aiApi.explain(docId, term, context, "auto", refresh, dtl);
            cache.set(key, r);
            setResp(r);
        } catch (e) { setError(true); }
        finally { setLoading(false); }
    }, [docId, term, context, lang, detail]);

    useEffect(() => { setSaved(false); setNoteOpen(false); setNote(""); fetchIt(false); }, [fetchIt]);

    const changeDetail = (d) => { setDetail(d); setAiDetail(d); fetchIt(false, d); };

    const data = resp?.data || {};
    const interest = resp?.interest || 0;

    const doSave = async () => {
        setBusy("save");
        try {
            const r = await termsApi.save({ document_id: docId, term, definition: data.definition });
            setSaved(r.saved);
            toast.success(r.saved ? t("explain.saved") : t("explain.save"));
        } catch (e) { toast.error(t("explain.error")); } finally { setBusy(""); }
    };
    const doNote = async () => {
        if (!note.trim()) { setNoteOpen(false); return; }
        setBusy("note");
        try {
            await termsApi.note({ document_id: docId, term, note: note.trim(), context, word_start: wordIndex });
            setSaved(true); setNoteOpen(false); toast.success(t("explain.noteSaved"));
        } catch (e) { toast.error(t("explain.error")); } finally { setBusy(""); }
    };
    const doFlashcard = async () => {
        setBusy("card");
        try {
            await flashcardsApi.add(docId, {
                front: term,
                back: data.definition || data.eli_new || "",
                topic: "Reading",
                type: "basic",
                hint: data.memory_tip || undefined,
            });
            toast.success(t("explain.addedFlashcard"));
        } catch (e) { toast.error(t("explain.error")); } finally { setBusy(""); }
    };

    return (
        <>
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--primary))]" />
                        <span className="mono-label text-t4">{t("explain.title")}</span>
                        {interest > 1 && (
                            <span data-testid="explain-interest" className="rounded-full bg-[hsl(var(--warning))]/15 px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--warning))]">
                                {t("explain.revisited", { n: interest })}
                            </span>
                        )}
                    </div>
                    <h3 data-testid="explain-term" title={term} className="mt-1 truncate font-sans-display text-lg font-semibold text-t1">{term}</h3>
                </div>
                <Button data-testid="explain-close-button" variant="ghost" size="icon" className="-mr-1 -mt-1 shrink-0 text-t3" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <div className="explain-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="explain-body">
                <div className="flex items-center justify-between gap-2">
                    <span className="mono-label text-t4">{t("detail.label")}</span>
                    <DetailLevel value={detail} onChange={changeDetail} compact />
                </div>
                {loading ? (
                    <div className="space-y-3">
                        <Skeleton className="h-4 w-3/4 bg-surface-3" />
                        <Skeleton className="h-12 w-full rounded-lg bg-surface-2" />
                        <Skeleton className="h-12 w-full rounded-lg bg-surface-2" />
                        <Skeleton className="h-4 w-1/2 bg-surface-3" />
                    </div>
                ) : error ? (
                    <div className="py-6 text-center">
                        <p className="text-sm text-t3">{t("explain.error")}</p>
                        <Button data-testid="explain-retry-button" size="sm" variant="secondary" className="mt-3 gap-1.5" onClick={() => fetchIt(true)}>
                            <RefreshCw className="h-3.5 w-3.5" /> {t("explain.retry")}
                        </Button>
                    </div>
                ) : (
                    <motion.div variants={fieldContainer} initial="hidden" animate="show" className="space-y-4">
                        {data.definition && <Field icon={BookOpen} label={t("explain.definition")} accent="hsl(174 72% 46%)">{data.definition}</Field>}
                        {data.eli_new && <Field icon={Lightbulb} label={t("explain.simply")} accent="hsl(38 82% 58%)">{data.eli_new}</Field>}
                        {data.why_matters && <Field icon={Target} label={t("explain.whyMatters")} accent="hsl(206 70% 55%)">{data.why_matters}</Field>}
                        {data.exam_relevance && <Field icon={GraduationCap} label={t("explain.examRelevance")} accent="hsl(330 62% 64%)">{data.exam_relevance}</Field>}
                        {data.memory_tip && <Field icon={Brain} label={t("explain.memoryTip")} accent="hsl(150 52% 52%)">{data.memory_tip}</Field>}

                        {data.deep_dive && (
                            <motion.div variants={fieldItem} className="rounded-xl border border-[hsl(var(--primary))]/25 bg-surface-2/50 p-3" data-testid="explain-deep-dive">
                                <div className="mono-label mb-1.5 flex items-center gap-1 text-[hsl(var(--primary))]"><GraduationCap className="h-3 w-3" /> {t("pro.deepDive")}</div>
                                <Markdown className="text-[14px] leading-relaxed text-t2">{data.deep_dive}</Markdown>
                            </motion.div>
                        )}

                        {data.related_concepts?.length > 0 && (
                            <motion.div variants={fieldItem}>
                                <div className="mono-label mb-1.5 text-t4">{t("explain.related")}</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {data.related_concepts.map((c, i) => (
                                        <button
                                            key={i}
                                            data-testid="explain-related-chip"
                                            onClick={() => onPickRelated?.(c)}
                                            className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-t2 transition-colors hover:border-[hsl(var(--primary))]/50 hover:text-t1"
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {context && (
                            <motion.div variants={fieldItem} className="rounded-xl border border-border bg-surface-2/50 px-3 py-2.5">
                                <div className="mono-label mb-1 text-t4">{t("explain.inPassage")}</div>
                                <p className="text-xs italic leading-relaxed text-t3">“{context.slice(0, 200)}{context.length > 200 ? "…" : ""}”</p>
                            </motion.div>
                        )}

                        {noteOpen && (
                            <div className="rounded-xl border border-border bg-surface-2 p-2.5">
                                <Textarea
                                    data-testid="explain-note-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder={t("explain.notePlaceholder")}
                                    className="min-h-[64px] resize-none bg-surface-1 text-sm"
                                    autoFocus
                                />
                                <div className="mt-2 flex justify-end gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => setNoteOpen(false)}>{t("common.cancel")}</Button>
                                    <Button data-testid="explain-note-save-button" size="sm" disabled={busy === "note"} onClick={doNote}>
                                        {busy === "note" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("explain.saveNote")}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {!loading && !error && (
                <div className="grid grid-cols-4 gap-1 border-t border-border p-2" data-testid="explain-actions">
                    <ActionBtn testid="explain-save-button" icon={Star} label={saved ? t("explain.saved") : t("explain.save")} active={saved} busy={busy === "save"} onClick={doSave} />
                    <ActionBtn testid="explain-note-button" icon={StickyNote} label={t("explain.note")} active={noteOpen} onClick={() => setNoteOpen((v) => !v)} />
                    <ActionBtn testid="explain-flashcard-button" icon={Layers} label={t("explain.flashcard")} busy={busy === "card"} onClick={doFlashcard} />
                    <ActionBtn testid="explain-ask-ai-button" icon={MessagesSquare} label={t("explain.askAI")} onClick={() => onAskAI?.(term, context)} />
                </div>
            )}
        </>
    );
}

const DesktopCard = ({ rect, onClose, children }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState({ top: -9999, left: -9999, ready: false });

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const reposition = () => {
            const w = el.offsetWidth || 380;
            const h = el.offsetHeight || 420;
            const pad = 12;
            if (!rect) {
                setPos({ top: clamp((window.innerHeight - h) / 2, pad, window.innerHeight - h - pad), left: clamp((window.innerWidth - w) / 2, pad, window.innerWidth - w - pad), ready: true });
                return;
            }
            const left = clamp(rect.left, pad, window.innerWidth - w - pad);
            const belowSpace = window.innerHeight - rect.bottom - pad;
            const aboveSpace = rect.top - pad;
            let top;
            if (belowSpace >= Math.min(h, 300) || belowSpace >= aboveSpace) {
                top = clamp(rect.bottom + 10, pad, window.innerHeight - h - pad);
            } else {
                top = clamp(rect.top - h - 10, pad, window.innerHeight - h - pad);
            }
            setPos({ top, left, ready: true });
        };
        reposition();
        const ro = new ResizeObserver(reposition);
        ro.observe(el);
        window.addEventListener("resize", reposition);
        return () => { ro.disconnect(); window.removeEventListener("resize", reposition); };
    }, [rect]);

    return (
        <>
            <div className="fixed inset-0 z-[70]" onMouseDown={onClose} data-testid="explain-backdrop" />
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: pos.ready ? 1 : 0, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
                style={{ position: "fixed", top: pos.top, left: pos.left, width: 380, maxHeight: "min(560px, 82vh)", zIndex: 71 }}
                className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface-1 shadow-elev-2"
                data-testid="explain-card"
                role="dialog"
                aria-modal="true"
            >
                {children}
            </motion.div>
        </>
    );
};

export const ExplainCard = ({ open, term, context, wordIndex, rect, docId, onClose, onAskAI, onPickRelated }) => {
    const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 639px)");
        const h = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", h);
        return () => mq.removeEventListener("change", h);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const body = (
        <ExplainBody docId={docId} term={term} context={context} wordIndex={wordIndex} onAskAI={onAskAI} onClose={onClose} onPickRelated={onPickRelated} />
    );

    return createPortal(
        <AnimatePresence>
            {open && term && (
                isMobile ? (
                    <React.Fragment key="mobile">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]" data-testid="explain-backdrop" />
                        <motion.div
                            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 320 }}
                            className="fixed inset-x-0 bottom-0 z-[71] flex max-h-[86vh] flex-col rounded-t-3xl border-t border-border bg-surface-1 shadow-elev-2"
                            data-testid="explain-card"
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className="mx-auto mb-1 mt-2 h-1 w-10 shrink-0 rounded-full bg-surface-3" />
                            {body}
                        </motion.div>
                    </React.Fragment>
                ) : (
                    <DesktopCard key="desktop" rect={rect} onClose={onClose}>{body}</DesktopCard>
                )
            )}
        </AnimatePresence>,
        document.body
    );
};
